import { camelizeKeys } from '@zoocasa/node-kit/objects';
import { Filter } from 'contexts/preferences/listing-params';
import { ThemeName } from 'themes';
import { SMART_TAGS_URL_SEARCH_PARAM } from 'types/SupportedURLSearchParam';
import { formatQueryIntoURLQueryParams, formatSmartTagsQueryValue } from 'utils/listing-query-helper';
import { ensureBackwardCompatibilityForHomeType } from './utils';

export type SavedSearch = {
  id: string;
  imageUrl: string | null;
  listingsCount: number | null;
  /**
   * Listings added within the window this search's alerts cover, cached
   * server-side. Null when alerts are off, so there is no window, or when the
   * server has not cached it yet.
   */
  newListingsCount: number | null;
  description: string;
  frequency: number;
  createdAt: Date;
  updatedOn: Date;
  path: string;
  source: ThemeName;
  formParams: {
    filter: Filter;
  };
};

export class SavedSearchImpl implements SavedSearch {
  id: string;
  imageUrl: string | null;
  listingsCount: number | null;
  newListingsCount: number | null;
  description: string;
  frequency: number;
  source: ThemeName;
  createdAt: Date;
  updatedOn: Date;
  formParams: { filter: Filter };
  
  constructor(savedSearch: Record<string, unknown>) {
    const camelizedSavedSearch = camelizeKeys(savedSearch);
    const attributes = camelizedSavedSearch.attributes as Record<string, unknown>;
    const relationships = camelizedSavedSearch.relationships as Record<
      string,
      unknown
    >;
    const formattedSavedSearch = {
      ...attributes,
      ...relationships,
      id: camelizedSavedSearch.id,
      createdAt: new Date(attributes.createdAt as string),
      updatedOn: new Date(attributes.updatedOn as string),
    } as SavedSearch;

    if (formattedSavedSearch?.formParams?.filter?.homeType) {
      const homeTypeFilter = ensureBackwardCompatibilityForHomeType(formattedSavedSearch.formParams.filter.homeType);
      formattedSavedSearch.formParams.filter.homeType = homeTypeFilter;
    }
    Object.assign(this, formattedSavedSearch);
  }

  /**
   * Builds a SavedSearchImpl from a storage-service (DynamoDB-backed) saved search.
   * The storage service speaks normalized JSON (camelCase, frequency already
   * converted to the numeric wire value), so we adapt it into the shape the
   * JSON:API constructor expects and reuse the same parsing/path logic.
   *
   * imageUrl/listingsCount/newListingsCount are not yet returned by the storage
   * service; they default to null until the worker populates listing metadata.
   */
  static fromStorage(item: {
    id: string;
    source: string;
    description: string;
    frequency: number;
    formParams: { filter: Filter };
    imageUrl?: string | null;
    listingsCount?: number | null;
    newListingsCount?: number | null;
    createdAt: string;
    updatedAt: string;
  }): SavedSearchImpl {
    return new SavedSearchImpl({
      id: item.id,
      attributes: {
        description: item.description,
        source: item.source,
        frequency: item.frequency,
        formParams: item.formParams,
        imageUrl: item.imageUrl ?? null,
        listingsCount: item.listingsCount ?? null,
        newListingsCount: item.newListingsCount ?? null,
        createdAt: item.createdAt,
        updatedOn: item.updatedAt,
      },
      relationships: {},
    });
  }

  get path() {
    return savedSearchUrl(this.formParams.filter);
  }
}
export function savedSearchUrl(filter: Filter): string {
  const slug = filter.slug?.trim();
  const query = savedSearchQuery(filter);
  if (slug) {
    const areaPath = `/${slug}-real-estate`;
    return query ? `${areaPath}/filter?${query}` : areaPath;
  }
  return query ? `/search?${query}` : '/search';
}

function savedSearchQuery(filter: Filter): string {
  const query = formatQueryIntoURLQueryParams(filter);
  const smartTags = formatSmartTagsQueryValue(filter.smartTagNames);
  if (!smartTags) {
    return query;
  }
  const smartTagsParam = `${SMART_TAGS_URL_SEARCH_PARAM}=${encodeURIComponent(smartTags)}`;
  return query ? `${query}&${smartTagsParam}` : smartTagsParam;
}